import { useMemo } from 'react'
import { useTurfMapDetail, useTurfMapHouseholds, useTurfMapContactsWithEmployment } from './useTurfMap'
import { groupContactsByWorkplace, splitByEmploymentStatus, contactsToHouseholdMarkers, applyTurfVisitedStatus } from '../components/turf/workplaceUtils'
import type { TurfBoundary, HouseholdMarker } from '../types/turfMap'
import type { HouseholdDto } from '../types/turf'
import type { WorkplaceMarker } from '../types/workplaceOverlay'
import type { OverlayMode } from '../types/workplaceOverlay'
import { TURF_COLORS } from '../lib/mapConfig'

export interface ContactLocation {
  id: string
  firstName: string
  lastName: string
  fullAddress: string
  streetAddress: string
  zip: string
  latitude: number
  longitude: number
  workLatitude?: number
  workLongitude?: number
}

export interface TurfMembership {
  contactIds: string[]
  householdCount: number
}

/**
 * Server-authoritative membership per turf id, from the households payload's per-contact turfId.
 * Households are counted once per turf, matching TurfMapOverviewPage's turfHouseholdCounts.
 */
export function turfMembershipFromHouseholds(households: HouseholdDto[] | undefined): Record<string, TurfMembership> {
  const membership: Record<string, TurfMembership> = {}
  for (const h of households ?? []) {
    const seen = new Set<string>()
    for (const c of h.contacts) {
      if (!c.turfId) continue
      const m = (membership[c.turfId] ??= { contactIds: [], householdCount: 0 })
      m.contactIds.push(c.contactId)
      if (!seen.has(c.turfId)) {
        seen.add(c.turfId)
        m.householdCount++
      }
    }
  }
  return membership
}

interface TurfReviewDataParams {
  isOpen: boolean
  turfMapId: string | undefined
  overlayMode: OverlayMode
}

export interface TurfReviewData {
  turfMapDetail: ReturnType<typeof useTurfMapDetail>['data']
  households: ReturnType<typeof useTurfMapHouseholds>['data']
  turfBoundaries: TurfBoundary[]
  householdMarkers: HouseholdMarker[]
  workplaceMarkers: WorkplaceMarker[] | undefined
  hybridHouseholds: HouseholdMarker[] | undefined
  contactLocations: ContactLocation[]
  turfMembership: Record<string, TurfMembership>
}

export function useTurfReviewData({ isOpen, turfMapId, overlayMode }: TurfReviewDataParams): TurfReviewData {
  const { data: turfMapDetail } = useTurfMapDetail(
    isOpen && turfMapId ? turfMapId : undefined
  )
  const { data: households } = useTurfMapHouseholds(
    isOpen && turfMapId ? turfMapId : undefined,
    true
  )

  const turfBoundaries: TurfBoundary[] = useMemo(() => {
    if (!turfMapDetail?.turfs?.length) return []
    return turfMapDetail.turfs.map((t, idx) => {
      try {
        const geometry = JSON.parse(t.boundaryGeoJson ?? '{}')
        return {
          id: t.id,
          label: t.label,
          color: TURF_COLORS[idx % TURF_COLORS.length] as string,
          geoJson: { type: 'Feature' as const, geometry, properties: {} },
        } as TurfBoundary
      } catch {
        return null
      }
    }).filter((b): b is TurfBoundary => b !== null)
  }, [turfMapDetail?.turfs])

  const householdMarkers: HouseholdMarker[] = useMemo(() => {
    if (!households?.length) return []
    return households.map(h => ({
      householdKey: h.householdKey,
      longitude: h.longitude,
      latitude: h.latitude,
      streetAddress: h.streetAddress,
      city: h.city,
      state: h.state,
      zip: h.zip,
      contactCount: h.contacts.length,
      visitedCount: h.contacts.filter(c => c.isVisited).length,
      needsRevisitCount: h.contacts.filter(c => c.isVisited && (c.contactStatus === 4 || c.contactStatus === 5)).length,
      contacts: h.contacts.map(c => ({
        contactId: c.contactId,
        contactName: c.contactName,
        walkOrder: c.walkOrder,
        isVisited: c.isVisited,
        contactStatus: c.contactStatus,
        turfId: c.turfId,
        turfIndex: c.turfIndex,
      })),
    }))
  }, [households])

  const { data: contactsWithEmployment } = useTurfMapContactsWithEmployment(
    isOpen && overlayMode !== 'home' ? turfMapDetail?.id : undefined,
    overlayMode !== 'home'
  )

  const enrichedEmployment = useMemo(
    () => contactsWithEmployment ? applyTurfVisitedStatus(contactsWithEmployment, households) : undefined,
    [contactsWithEmployment, households]
  )

  const { workplaceMarkers, hybridHouseholds } = useMemo(() => {
    if (!enrichedEmployment || overlayMode === 'home') {
      return { workplaceMarkers: undefined, hybridHouseholds: undefined }
    }
    if (overlayMode === 'work') {
      return {
        workplaceMarkers: groupContactsByWorkplace(enrichedEmployment),
        hybridHouseholds: undefined,
      }
    }
    const { withWork, withoutWork } = splitByEmploymentStatus(enrichedEmployment)
    return {
      workplaceMarkers: groupContactsByWorkplace(withWork),
      hybridHouseholds: contactsToHouseholdMarkers(withoutWork),
    }
  }, [enrichedEmployment, overlayMode])

  const contactLocations: ContactLocation[] = useMemo(() => {
    if (!households?.length) return []
    const workLookup = new Map<string, { lat: number; lng: number }>()
    if (contactsWithEmployment) {
      for (const c of contactsWithEmployment) {
        const emp = c.employments?.find(e => e.isPrimary && e.worksiteLatitude != null)
          ?? c.employments?.find(e => e.worksiteLatitude != null)
        if (emp?.worksiteLatitude != null && emp?.worksiteLongitude != null) {
          workLookup.set(c.id, { lat: emp.worksiteLatitude, lng: emp.worksiteLongitude })
        }
      }
    }
    return households.flatMap(h =>
      h.contacts.map(c => {
        const work = workLookup.get(c.contactId)
        return {
          id: c.contactId,
          firstName: c.contactName.split(' ')[0] ?? '',
          lastName: c.contactName.split(' ').slice(1).join(' ') ?? '',
          fullAddress: `${h.streetAddress}, ${h.city}`,
          streetAddress: h.streetAddress,
          zip: h.zip,
          latitude: h.latitude,
          longitude: h.longitude,
          workLatitude: work?.lat,
          workLongitude: work?.lng,
        }
      })
    )
  }, [households, contactsWithEmployment])

  const turfMembership = useMemo(() => turfMembershipFromHouseholds(households), [households])

  return {
    turfMapDetail,
    households,
    turfBoundaries,
    householdMarkers,
    workplaceMarkers,
    hybridHouseholds,
    contactLocations,
    turfMembership,
  }
}
